import React, { useLayoutEffect, useRef, useState } from "react"
import "react-grid-layout/css/styles.css"
import ReactGridLayout, { type Layout } from "react-grid-layout/legacy"

import {
  HOME_GRID_COLUMNS,
  type HomeLayoutItem,
  readLayout
} from "./homeLayout"
import {
  HOME_SECTION_MARGIN,
  HOME_SECTION_ROW_HEIGHT,
  isSectionItem,
  moveSection,
  readingOrder,
  sectionIndexForDrop,
  sectionOfItem,
  settleDisplaced,
  swapOnDrop
} from "./homeSectionLayout"
import { GRID_HANDLE_CLASS } from "./WidgetEditChrome"

/**
 * Below this width the grid is not drawn: the page stacks in reading order.
 *
 * The stacked mode never reports a layout, so a phone (or a narrow window)
 * cannot rewrite the desktop arrangement — widening the window again draws
 * the same saved geometry. Storing a second, per-breakpoint arrangement would
 * instead let every narrow visit quietly overwrite it.
 */
export const HOME_SECTION_GRID_MIN_WIDTH = 900

/**
 * The grid's own width, from a ResizeObserver on its container.
 *
 * react-grid-layout's `WidthProvider` listens to `window` resizes only, so
 * collapsing the sidebar — which changes the content column and not the
 * window — left every widget laid out for the old width until the next
 * window resize.
 */
const useContainerWidth = () => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    setWidth(Math.round(element.getBoundingClientRect().width))
    if (typeof ResizeObserver !== "function") return
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width
      if (typeof next === "number") setWidth(Math.round(next))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])
  return { ref, width }
}

interface HomeSectionGridProps {
  layout: HomeLayoutItem[]
  /** A widget's minimum size, so neither a drag nor a resize goes below it. */
  minSizeOf: (id: string) => { w: number; h: number }
  /** Sized by its content: only its width is offered as a resize. */
  isAutoHeight?: (id: string) => boolean
  isEditing: boolean
  /** A finished drag or resize, already normalised into clean sections. */
  onCommit: (next: HomeLayoutItem[]) => void
  renderItem: (id: string) => React.ReactNode
}

export const HomeSectionGrid = ({
  layout,
  minSizeOf,
  isAutoHeight,
  isEditing,
  onCommit,
  renderItem
}: HomeSectionGridProps) => {
  const { ref, width } = useContainerWidth()
  const stacked = width < HOME_SECTION_GRID_MIN_WIDTH

  const gridLayout: Layout = layout.map((item) => {
    if (isSectionItem(item.i)) {
      return { ...item, minW: HOME_GRID_COLUMNS, isResizable: false }
    }
    const min = minSizeOf(item.i)
    return isAutoHeight?.(item.i)
      ? { ...item, minW: min.w, minH: 1, resizeHandles: ["e"] }
      : { ...item, minW: min.w, minH: min.h }
  })

  const handleDragStop = (
    next: Layout,
    _old: unknown,
    moved: { i: string; x: number; y: number } | null
  ) => {
    const section = moved ? sectionOfItem(moved.i) : null
    if (section && moved) {
      // A heading carries its whole section. Where it belongs is read from
      // the page as it was before the drag: the grid has shuffled the other
      // headings around the dragged one by now.
      onCommit(
        moveSection(
          layout,
          section,
          sectionIndexForDrop(layout, section, moved.y)
        )
      )
      return
    }
    onCommit(
      (moved && swapOnDrop(layout, moved.i, moved.x, moved.y)) ??
        settleDisplaced(layout, readLayout(next), moved?.i ?? "")
    )
  }

  return (
    <div
      ref={ref}
      className={`home-section-grid${isEditing ? " home-section-grid--editing" : ""}`}
      data-testid="home-section-grid"
      data-layout-mode={stacked ? "stacked" : "grid"}
    >
      {stacked ? (
        <div className="home-section-grid__stack">
          {readingOrder(layout).map((item) => (
            <div
              key={item.i}
              data-home-item={item.i}
              className={
                isSectionItem(item.i)
                  ? "home-section-grid__heading"
                  : "home-section-grid__unit"
              }
            >
              {renderItem(item.i)}
            </div>
          ))}
        </div>
      ) : width > 0 ? (
        <ReactGridLayout
          width={width}
          layout={gridLayout}
          cols={HOME_GRID_COLUMNS}
          rowHeight={HOME_SECTION_ROW_HEIGHT}
          margin={HOME_SECTION_MARGIN}
          containerPadding={[0, 0]}
          // Only the handle starts a drag, so a click on a widget's own
          // controls (or a record row inside it) is never a drag.
          draggableHandle={`.${GRID_HANDLE_CLASS}`}
          isDraggable={isEditing}
          isResizable={isEditing}
          resizeHandles={["se", "e", "s"]}
          compactType="vertical"
          preventCollision={false}
          allowOverlap={false}
          useCSSTransforms
          onDragStop={handleDragStop}
          onResizeStop={(next, _old, resized) =>
            onCommit(
              settleDisplaced(layout, readLayout(next), resized?.i ?? "")
            )
          }
        >
          {layout.map((item) => (
            <div
              key={item.i}
              data-home-item={item.i}
              className={
                isSectionItem(item.i)
                  ? "home-section-grid__heading"
                  : "home-section-grid__unit"
              }
            >
              {renderItem(item.i)}
            </div>
          ))}
        </ReactGridLayout>
      ) : null}
    </div>
  )
}
